import {
  EProjectType,
  EUserAccessPart,
  IComponentSet,
  IComponentSetDto,
  IComponentSetTheme,
  IFillStyle,
  IProjectDto,
  IStylingBorder,
  IStylingEffects,
  IStylingPosition,
  IStylingShadow,
  IStylingTypography,
  IUserDto,
  IUserProjectRole,
  IWorkspaceProjectRole
} from "@kuika/kuika-cl-model";
import { IProjectsResponseDto } from "@kuika/kuika-cl-model/src/dtos/projects-response";
import { Form, FormInstance } from "antd/es";
import jsonStringifySafe from "json-stringify-safe";
import _ from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { ComponentSetService } from "../../../../services/component-set-service";
import { DefaultStyleService, IAllDefaultStylesResponse } from "../../../../services/default-style-service";
import { DomainService } from "../../../../services/domain-service";
import { ProjectService } from "../../../../services/project-service";
import { UserService } from "../../../../services/user-service";
import confJson from "../../../../shared/data/conf.json";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import iconData from "../../../../shared/icon/icons.json";
import { KIcon } from "../../../../shared/icon/k-icon";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDrawer from "../../../ui-kit/kuika-drawer/kuika-drawer";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallBoldText from "../../../ui-kit/typography/SmallBoldText/SmallBoldText";
import ProjectDefinitionAppType from "./project-definition-app-type";
import ProjectDefinitionIconPopover from "./project-definition-icon-popover";
import ProjectDefinitionShare from "./project-definition-share";
// Styling section disabled while it's being reworked.
// import ProjectDefinitionStyling from "./project-definition-styling";
// import { deriveStylingSelections } from "./project-definition-styling-utils";
import "./project-definition-add-with-project-type.scss";

const { kuikaDevelopers } = confJson;

/** What each commit is doing, rather than the generic "Saving". */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };
const UPDATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Updating", success: "Updated" };

export interface IProjectDefinitionAddWithProjectTypeState {
  componentSetList: IComponentSetDto;
  selectedComponentSetId: string | undefined;
  /** Where Create / Update stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  isLoading: boolean;
  // Candidate user-list load (Share section). Kept separate from isLoading so it can't
  // prematurely unlock the drawer/Create button while the essential settings load runs.
  isUserListLoading: boolean;
  projectJson?: any;
  projectType?: EProjectType;
  userList: IUserDto[];
  projectRoleList: IWorkspaceProjectRole[];
  selectedUsersToShare: IUserProjectRole[];
  isPrivate: boolean;
  iconBackgroundColor: string;
  iconColor: string;
  iconName: string;
  selectedImage: string;
  projectSchema?: string;
  isolatedDbSchemaForProjectsEnabled: boolean;
  isDataLoaded: boolean;
  userCreatedTableAvailable: boolean;
  iconPopoverOpen: boolean;
  stylingFont: string;
  stylingPrimaryColor: string;
  stylingRadius: number;
  defaultStyles?: IAllDefaultStylesResponse;
  fontOptions: string[];
  // Manually managed App Name validation (shown inline to the right of the input,
  // red border via KuikaInput's error prop). null when the name is valid.
  nameError: string | null;
  // Manually managed database schema validation (shown to the right of the chip,
  // red chip border via the --error modifier). null when the schema is valid.
  schemaError: string | null;
}

export interface IProjectDefinitionAddWithProjectTypeProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  closeModal: any;
  showModal: boolean;
  projectToEdit: IProjectsResponseDto;
  updateUserInfo: () => void;
  projectType?: EProjectType;
  templateProjectId?: string;
}

class ProjectDefinitionAddWithProjectType extends React.PureComponent<
  IProjectDefinitionAddWithProjectTypeProps & IWithContextProp & IWithHistoryProp,
  IProjectDefinitionAddWithProjectTypeState
> {
  private formRef = React.createRef<FormInstance>();

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IProjectDefinitionAddWithProjectTypeProps & IWithContextProp & IWithHistoryProp) {
    super(props);
    this.state = {
      componentSetList: {} as IComponentSetDto,
      projectSchema: undefined,
      saveStatus: "idle",
      selectedComponentSetId: undefined,
      isLoading: false,
      isUserListLoading: false,
      projectJson: undefined,
      projectType: this.props.projectType ?? EProjectType.Desktop,
      userList: [],
      projectRoleList: [],
      selectedUsersToShare: [],
      isPrivate: true,
      // Edit: seed the icon from projectToEdit (already on the prop) so the real icon shows
      // immediately instead of flashing a random default until loadProjectSettings resolves.
      iconName: props.projectToEdit?.iconName || ProjectDefinitionAddWithProjectType.getRandomIcon(),
      iconBackgroundColor: props.projectToEdit?.iconBackgroundColor || KMainFunctions.generateRandomHEXColor(),
      iconColor: props.projectToEdit?.iconColor || "#FFF",
      selectedImage: props.projectToEdit?.iconAsImage || "",
      isolatedDbSchemaForProjectsEnabled: false,
      isDataLoaded: false,
      userCreatedTableAvailable: false,
      iconPopoverOpen: false,
      stylingFont: "Poppins",
      stylingPrimaryColor: "#0358FF",
      stylingRadius: 8,
      defaultStyles: undefined,
      fontOptions: [],
      nameError: null,
      schemaError: null
    };
  }

  validateName = (name: string | undefined): string | null => {
    const trimmed: string = (name ?? "").trim();
    if (!trimmed) {
      return "Please enter a project name!";
    }
    if (!/^[A-Za-z][A-Za-z0-9]*$/.test(trimmed)) {
      return "Project name must contains A-Z or a-z or 0-9.";
    }
    if (trimmed.length > 24) {
      return "Project name must be max 24 characters.";
    }
    return null;
  };

  validateSchema = (schema: string | undefined): string | null => {
    const trimmed: string = (schema ?? "").trim();
    if (!trimmed) {
      return "Please enter a database schema!";
    }
    if (trimmed.length < 3 || trimmed.length > 6) {
      return "Database schema must be 6 characters.";
    }
    return null;
  };

  public static getRandomIcon() {
    const icons = iconData.icons;

    return icons[Math.floor(Math.random() * icons.length)]?.name;
  }

  isKuikaDeveloper = (): boolean => {
    return KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username);
  };

  submitFormData = async (data: any) => {
    this.setState({ isLoading: true });
    let json = {
      componentSetObj: "",
      stylingTypography: [] as IStylingTypography[],
      stylingPosition: [] as IStylingPosition[],
      stylingBorder: [] as IStylingBorder[],
      stylingShadow: [] as IStylingShadow[],
      stylingFill: [] as IFillStyle[],
      stylingEffects: [] as IStylingEffects[],
      projectType: this.state.projectType
    };
    if (this.props.projectToEdit.id) {
      json = this.state.projectJson;
      json.projectType = this.state.projectJson.projectType;
    }
    const compSetObj = this.state.componentSetList;
    json.componentSetObj = compSetObj?.id;
    if (_.isEmpty(this.props.projectToEdit) && !this.props.templateProjectId) {
      // Skip style fetch for template projects: the backend overwrites the JSON
      // with the template's JSON anyway, so these styles would be discarded.
      // For non-template projects, use the single /defaultstyle/all endpoint (1 call instead of 10).
      try {
        let fetchedStyles: IAllDefaultStylesResponse;
        if (this.state.defaultStyles) {
          fetchedStyles = this.state.defaultStyles;
        } else {
          fetchedStyles = await DefaultStyleService.getAllDefaultStyles();
        }
        // Styling section disabled while reworked; seed with the raw default styles.
        const allStyles: IAllDefaultStylesResponse = fetchedStyles;

        json.stylingTypography = (allStyles.typography as IStylingTypography[]).concat(
          allStyles.typographyMobile as IStylingTypography[]
        );
        json.stylingPosition = (allStyles.position as IStylingPosition[]).concat(
          allStyles.positionMobile as IStylingPosition[]
        );
        json.stylingBorder = (allStyles.border as IStylingBorder[]).concat(allStyles.borderMobile as IStylingBorder[]);
        json.stylingShadow = (allStyles.shadow as IStylingShadow[]).concat(allStyles.shadowMobile as IStylingShadow[]);
        json.stylingFill = (allStyles.fill as IFillStyle[]).concat(allStyles.fillMobile as IFillStyle[]);
      } catch (error) {
        console.error("Failed to load default styles:", error);
        KMainFunctions.exceptionHandler(error);
        this.setState({ isLoading: false });
        this.saveStatus.fail();
        return;
      }
    }
    const postObj = {} as IProjectDto;
    postObj.json = jsonStringifySafe(json);
    postObj.name = data.name.trim();
    postObj.schemaName = data.schema?.trim();
    postObj.description = data.description;
    postObj.iconAsImage = this.state.selectedImage;
    postObj.iconName = this.state.iconName;
    postObj.iconColor = this.state.iconColor;
    postObj.iconBackgroundColor = this.state.iconBackgroundColor;
    postObj.isPrivate = this.state.isPrivate;
    postObj.userProjectRoles = this.state.selectedUsersToShare;
    if (this.props.templateProjectId && this.props.templateProjectId.length > 0)
      postObj.templateProjectId = this.props.templateProjectId;

    // Every exit below ends the save machine: the ✓ is held before the drawer leaves (and, for a
    // new app, before the designer opens it); anything else puts the failure on the button.
    if (this.props?.projectToEdit?.id) {
      if (postObj.name === "") {
        KMainFunctions.displayWarningNotification("App name must not be blank");
        this.saveStatus.fail();
      } else {
        const res = await ProjectService.Update(this.props.projectToEdit.id, postObj);
        if (res === true) {
          this.props.updateUserInfo();
          this.saveStatus.succeed((): void => this.props.closeModal(true));
        } else {
          this.saveStatus.fail();
        }
      }
    } else {
      try {
        const res: any = await ProjectService.Create(postObj);

        if (res?.status === 200) {
          this.props.updateUserInfo();
          this.saveStatus.succeed((): void => {
            this.props.closeModal(true);
            ProjectService.GenerateProjectForInitialization(res.data.id);
            this.props.navigator.gotoScreen(res.data?.defaultScreenDesignId!);
          });
        } else {
          this.saveStatus.fail();
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message || "";
        this.handleResponseError(errorMessage);
        this.saveStatus.fail();
      }
    }

    this.setState({ isLoading: false });
  };

  handleResponseError = (errorMessage: string) => {
    if (errorMessage.includes("is already taken") || errorMessage.includes("Project name is invalid.")) {
      KMainFunctions.warningHandler({
        response: { data: { message: errorMessage } }
      });
    } else {
      KMainFunctions.exceptionHandler({
        response: { data: { message: errorMessage } }
      });
    }
  };

  onSubmit = async (data: any) => {
    const nameError: string | null = this.validateName(data?.name);
    if (nameError) {
      this.setState({ nameError });
      document.getElementById("newAppForm_name")?.focus();
      return;
    }

    let submitData = data;
    if (this.state.isolatedDbSchemaForProjectsEnabled) {
      // Auto-generate the schema from the name when it was never set (mirrors the old
      // onFinishFailed fallback), then validate it.
      if (submitData?.schema == null && submitData?.name) {
        const generatedSchema: string = await ProjectService.GenerateSchema(submitData.name);
        if (generatedSchema) {
          this.formRef.current?.setFieldsValue({ schema: generatedSchema });
          submitData = { ...submitData, schema: generatedSchema };
        }
      }
      const schemaError: string | null = this.validateSchema(submitData?.schema);
      if (schemaError) {
        this.setState({ schemaError });
        document.getElementById("newAppForm_schema")?.focus();
        return;
      }
    }

    // A second submit while the app is saved or its ✓ is held would save it twice.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    try {
      this.setState({ nameError: null, schemaError: null });
      await this.submitFormData(submitData);
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
      this.setState({ isLoading: false });
      this.saveStatus.fail();
    }
  };

  /** Cancel, the X and Escape. During the ✓ hold the drawer is already on its way out. */
  handleClose = (): void => {
    if (this.saveStatus.current === "success") return;
    this.props.closeModal();
  };

  setPrivacity = (value: string) => {
    const isPrivate = value === "private";
    this.setState({ isPrivate });
  };

  async componentDidMount() {
    await this.loadProjectSettings();
  }

  loadProjectSettings() {
    this.setState({ isLoading: true });

    const hasAccessEditProjectRole = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.EditProjectRole);
    const hasAccessEditProjectRoleInProject = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.EditProjectRole,
      this.props.projectToEdit.projectAccess
    );
    const isDomainAuthorizationEnabled = KMainFunctions.isDomainAuthorizationEnabled();
    const isEditing = this.props?.projectToEdit?.id;

    Promise.allSettled([
      DomainService.GetCurrentDomainDetail(),
      this.props.projectToEdit?.id
        ? ProjectService.GetProjectJSONById(this.props.projectToEdit.id)
        : { status: 200, data: {} },
      ComponentSetService.Get("4bd34537-525c-4a7c-dde0-08d911312c71"),
      isDomainAuthorizationEnabled && (hasAccessEditProjectRole || hasAccessEditProjectRoleInProject || !isEditing)
        ? ProjectService.GetProjectRoleList()
        : Promise.resolve({ status: 200, data: [] }),
      !isEditing && !this.props.templateProjectId
        ? DefaultStyleService.getAllDefaultStyles().then((data) => ({ status: 200, data }))
        : Promise.resolve({ status: 200, data: null })
    ])
      .then((results: any) => {
        if (results.find((result) => result.status === "rejected" || result.value.status !== 200) as any) {
          this.props.closeModal();
          return;
        }

        let {
          isolatedDbSchemaForProjectsEnabled,
          userCreatedTableAvailable,
          isPrivate,
          selectedImage,
          iconName,
          iconColor,
          iconBackgroundColor,
          selectedUsersToShare,
          componentSetList,
          selectedComponentSetId,
          projectJson,
          projectType,
          projectRoleList
          // userList
        } = this.state;

        // RESULTS[0]
        isolatedDbSchemaForProjectsEnabled = results[0].value.data.isolatedDbSchemaForProjectsEnabled;
        if (this.props.projectToEdit?.id) {
          userCreatedTableAvailable = results[0].value.data.userCreatedTableAvailable;
        }

        // RESULTS[2]
        componentSetList = results[2].value.data as IComponentSetDto;

        // RESULTS[4] — styling defaults + available fonts (create-from-scratch only)
        const defaultStyles: IAllDefaultStylesResponse | null = results[4]?.value?.data ?? null;
        const stylingFont: string = this.state.stylingFont;
        const stylingPrimaryColor: string = this.state.stylingPrimaryColor;
        const stylingRadius: number = this.state.stylingRadius;
        let fontOptions: string[] = this.state.fontOptions;
        // Styling selections derive disabled while the Styling section is being reworked.
        try {
          const parsedComponentSet: IComponentSet = JSON.parse(componentSetList.json);
          if (parsedComponentSet?.fontNames?.length) fontOptions = parsedComponentSet.fontNames;
        } catch {
          // component set json malformed — keep existing font options
        }

        // RESULTS[3]
        projectRoleList = results[3].value.data as IWorkspaceProjectRole[];

        if (this.props.projectToEdit?.id) {
          const data = this.props.projectToEdit;
          isPrivate = data.isPrivate;
          selectedImage = data.iconAsImage;
          iconName = data.iconName || iconName;
          iconColor = data.iconColor || iconColor;
          iconBackgroundColor = data.iconBackgroundColor || iconBackgroundColor;
          selectedUsersToShare = data.userProjectRoles?.map((user: any) => {
            return {
              userId: user.userId,
              projectRoleId: user.roleId
            };
          });

          // RESULTS[1]
          projectJson = results[1].value.data;
          projectType = projectJson.projectType ?? EProjectType.Desktop;
          selectedComponentSetId = projectJson.componentSetObj;
          if (!selectedComponentSetId) {
            selectedComponentSetId = componentSetList.id;
          }
        }
        this.setState({
          isPrivate,
          selectedImage,
          iconName,
          iconColor,
          iconBackgroundColor,
          // RESULTS[0]
          selectedUsersToShare,
          // RESULTS[1]
          selectedComponentSetId,
          projectJson,
          projectType,
          // RESULTS[2]
          componentSetList,
          // RESULTS[3]
          projectRoleList,
          isolatedDbSchemaForProjectsEnabled,
          userCreatedTableAvailable,
          defaultStyles: defaultStyles ?? undefined,
          stylingFont,
          stylingPrimaryColor,
          stylingRadius,
          fontOptions
        });
      })
      .catch((error) => {
        KMainFunctions.exceptionHandler(error);
        this.props.closeModal();
      })
      .finally(() => {
        this.setState({ isLoading: false });
      });
  }

  loadUserList = async () => {
    try {
      // Own flag (not isLoading): the candidate list is not needed to create a project, so
      // it must not gate the drawer/Create button — only loadProjectSettings + submit do.
      this.setState({ isUserListLoading: true });
      let userList: IUserDto[] = (await (await UserService.GetUsersByDomainId()).data) as IUserDto[];
      userList = userList?.filter((user) => user.invitationStatus === null);
      userList?.filter((user) => user.id !== this.props.context.userInfo.id);
      this.setState({
        userList
      });
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({ isUserListLoading: false });
    }
  };

  onBlurOfProjectName = async (e) => {
    if (e?.target?.value) {
      const formValues = this.formRef.current?.getFieldsValue();
      // if (formValues.name === e.target.value) return;
      if (formValues.schema == null) {
        const projectSchema = await ProjectService.GenerateSchema(e.target.value);
        if (projectSchema) {
          this.formRef.current?.setFieldsValue({
            ...formValues,
            schema: projectSchema
          });
          if (this.state.schemaError) this.setState({ schemaError: null });
        }
      }
    }
  };

  componentWillUnmount() {
    this.setState({ isDataLoaded: false });
    this.saveStatus.dispose();
  }

  handleFormValuesChange = (changedValues: any) => {
    const formFieldName = Object.keys(changedValues)[0];
    if (formFieldName === "componentSet") {
      const componentSetId = changedValues[formFieldName];
      const { componentSetList } = this.state;
      let themeList: IComponentSetTheme[] = [];
      if (componentSetList && componentSetId) {
        const selectedComponentSet = componentSetList;
        if (selectedComponentSet && selectedComponentSet.json) {
          const parsedJson: IComponentSet = JSON.parse(selectedComponentSet.json);
          if (parsedJson.themes) {
            themeList = parsedJson.themes;
          }
        }
      }
      this.setState({
        selectedComponentSetId: componentSetId
      });
    }
  };

  changeProjectType = (value: EProjectType) => {
    this.setState({ projectType: value });
  };

  setSelectedIcon = (val: string) => {
    this.setState({ iconName: val });
  };

  setSelectedIconColor = (val: string) => {
    this.setState({ iconColor: val });
  };

  setSelectedImage = (val: string) => {
    this.setState({ selectedImage: val });
  };

  setSelectedIconBackgroundColor = (val: string) => {
    this.setState({ iconBackgroundColor: val });
  };

  addSelectedUsersToShare = (user: IUserProjectRole) => {
    this.setState({
      selectedUsersToShare: [...this.state.selectedUsersToShare, user]
    });
  };

  removeSelectedUsersToShare = (user: IUserProjectRole) => {
    this.setState({
      selectedUsersToShare: this.state.selectedUsersToShare.filter((u) => u.userId !== user.userId)
    });
  };

  updateSelectedUsersToShareUserRole = (user: IUserProjectRole, roleId: string) => {
    const updatedSelectedUsersToShare = this.state.selectedUsersToShare?.map((u) => {
      if (u.userId === user.userId) {
        return { ...u, projectRoleId: roleId };
      }
      return u;
    });
    this.setState({ selectedUsersToShare: updatedSelectedUsersToShare });
  };

  renderChoosePreview = () => {
    if (this.state.selectedImage) {
      return <img src={this.state.selectedImage} alt="app-icon" className="kuika-new-app__choose-img" />;
    }
    if (this.state.iconName) {
      return (
        <span className="kuika-new-app__choose-icon" style={{ backgroundColor: this.state.iconBackgroundColor }}>
          <KIcon iconName={this.state.iconName} style={{ color: this.state.iconColor }} />
        </span>
      );
    }
    return (
      <span className="kuika-new-app__choose-empty">
        <KuikaSvg name="plus" width={20} height={20} color="var(--dark-7)" />
        <span className="kuika-new-app__choose-label">CHOOSE</span>
      </span>
    );
  };

  render() {
    const hasAccessEditIcon = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.UpdateProjectIcon);
    const hasAccessEditIconInProject = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.UpdateProjectIcon,
      this.props.projectToEdit.projectAccess
    );
    const hasAccessEditProjectRole = KMainFunctions.hasUserAccessInWorkspace(EUserAccessPart.EditProjectRole);
    const hasAccessEditProjectRoleInProject = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.EditProjectRole,
      this.props.projectToEdit.projectAccess
    );
    const hasAccessChangeProjectVisibility = KMainFunctions.hasUserAccessInWorkspace(
      EUserAccessPart.ChangeProjectVisibility
    );
    const isDomainAuthorizationEnabled = KMainFunctions.isDomainAuthorizationEnabled();
    const isEditing = this.props?.projectToEdit?.id;
    const canEditIcon: boolean = hasAccessEditIcon || hasAccessEditIconInProject || !isEditing;
    return (
      <KuikaDrawer
        isOpen={this.props.showModal}
        onClose={this.handleClose}
        header={<KuikaSmallBoldText>{isEditing ? "EDIT APP" : "CREATE NEW APP"}</KuikaSmallBoldText>}
        isLoading={this.state.isLoading}
        width={602}
        bodyClassName="kuika-new-app-drawer"
        afterOpenChange={(open: boolean) => {
          // Focus the App Name field only after the open transition ends. Focusing
          // earlier (e.g. autoFocus) blurs when the drawer grabs focus mid-animation,
          // and that stray blur used to regenerate an existing project's schema.
          if (open) document.getElementById("newAppForm_name")?.focus();
        }}
        footer={
          <>
            <KuikaButton
              type="text"
              color="neutral"
              size="lg"
              uppercase
              data-testid="cancelButton"
              onClick={this.handleClose}
            >
              Cancel
            </KuikaButton>
            <KuikaButton
              type="primary"
              size="lg"
              uppercase
              htmlType="submit"
              form="newAppForm"
              data-testid="updateCreateButton"
              // Held while the app's settings load; during the save the button says so itself.
              disabled={this.state.isLoading && this.state.saveStatus === "idle"}
              status={this.state.saveStatus}
              statusLabels={this.props.projectToEdit?.id ? UPDATE_STATUS_LABELS : CREATE_STATUS_LABELS}
            >
              {this.props.projectToEdit?.id ? "Update" : "Create App"}
            </KuikaButton>
          </>
        }
      >
        <Form
          ref={this.formRef}
          onValuesChange={this.handleFormValuesChange}
          name="newAppForm"
          onFinish={this.onSubmit}
          initialValues={{
            name: this.props?.projectToEdit?.name,
            schema: this.props?.projectToEdit?.schemaName
          }}
        >
          <div className="kuika-new-app">
            <div className="kuika-new-app__header">
              {canEditIcon ? (
                <ProjectDefinitionIconPopover
                  open={this.state.iconPopoverOpen}
                  onOpenChange={(visible: boolean) => this.setState({ iconPopoverOpen: visible })}
                  iconName={this.state.iconName}
                  iconColor={this.state.iconColor}
                  iconBackgroundColor={this.state.iconBackgroundColor}
                  selectedImage={this.state.selectedImage}
                  setSelectedIcon={this.setSelectedIcon}
                  setSelectedIconColor={this.setSelectedIconColor}
                  setSelectedIconBackgroundColor={this.setSelectedIconBackgroundColor}
                  setSelectedImage={this.setSelectedImage}
                >
                  <button type="button" data-testid="chooseIconButton" className="kuika-new-app__choose">
                    {this.renderChoosePreview()}
                    {(this.state.selectedImage || this.state.iconName) && (
                      <span className="kuika-new-app__choose-overlay" aria-hidden>
                        <KuikaSvg name="plus" width={16} height={16} color="var(--white)" />
                        <span className="kuika-new-app__choose-label">CHOOSE</span>
                      </span>
                    )}
                  </button>
                </ProjectDefinitionIconPopover>
              ) : (
                <div className="kuika-new-app__choose kuika-new-app__choose--readonly">
                  {this.renderChoosePreview()}
                </div>
              )}
              <div className="kuika-new-app__meta">
                <div className="kuika-new-app__field">
                  <span className="kuika-new-app__field-label">App Name</span>
                  <Form.Item name="name" noStyle>
                    <KuikaInput
                      data-testid="appNameInput"
                      size="sm"
                      maxLength={24}
                      placeholder="App Name"
                      error={!!this.state.nameError}
                      className="kuika-new-app__field-input"
                      onChange={() => {
                        if (this.state.nameError) this.setState({ nameError: null });
                      }}
                      onBlur={(e) => this.onBlurOfProjectName(e)}
                    />
                  </Form.Item>
                  {this.state.nameError && <span className="kuika-new-app__field-error">{this.state.nameError}</span>}
                </div>
                {this.state.isolatedDbSchemaForProjectsEnabled && (
                  <div className="kuika-new-app__field">
                    <span className="kuika-new-app__field-label">Schema</span>
                    <Form.Item name="schema" noStyle>
                      <KuikaInput
                        data-testid="dbSchemaNameInput"
                        size="sm"
                        maxLength={6}
                        placeholder="new_app"
                        readOnly={this.state.userCreatedTableAvailable}
                        error={!!this.state.schemaError}
                        className="kuika-new-app__field-input"
                        onChange={() => {
                          if (this.state.schemaError) this.setState({ schemaError: null });
                        }}
                      />
                    </Form.Item>
                    {this.state.schemaError && (
                      <span className="kuika-new-app__field-error">{this.state.schemaError}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            <ProjectDefinitionAppType
              projectType={this.state.projectType}
              changeProjectType={this.changeProjectType}
              isEditing={
                this.props.projectToEdit.templateProjectId !== undefined ? true : !!this.props.projectToEdit?.id
              }
            />

            {/* Styling section disabled while it's being reworked; ProjectDefinitionStyling
                used to render here. The divider stays as the section separator. */}
            {!isEditing && !this.props.templateProjectId && <div className="kuika-new-app__divider" />}

            {isDomainAuthorizationEnabled &&
              (hasAccessEditProjectRole || hasAccessEditProjectRoleInProject || !isEditing) && (
                <ProjectDefinitionShare
                  projectOwnerId={this.props.projectToEdit?.projectOwner?.id ?? this.props.context.userInfo.id}
                  userList={this.state.userList}
                  projectRoleList={this.state.projectRoleList}
                  selectedUsersToShare={this.state.selectedUsersToShare}
                  addSelectedUserToShare={this.addSelectedUsersToShare}
                  removeSelectedUserToShare={this.removeSelectedUsersToShare}
                  updateSelectedUsersToShareUserRole={this.updateSelectedUsersToShareUserRole}
                  isPrivate={this.state.isPrivate}
                  setPrivacity={this.setPrivacity}
                  projectToEdit={this.props.projectToEdit}
                  handleLoadUsersList={this.loadUserList}
                  isUserListLoading={this.state.isUserListLoading}
                />
              )}
          </div>
        </Form>
      </KuikaDrawer>
    );
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const projectDefinitionAddWithProjectType = connect(
  null,
  mapDispatchToProps
)(
  withContext<IProjectDefinitionAddWithProjectTypeProps>(
    withHistory<IProjectDefinitionAddWithProjectTypeProps & IWithContextProp & IWithHistoryProp>(
      ProjectDefinitionAddWithProjectType
    )
  )
);
export { projectDefinitionAddWithProjectType as ProjectDefinitionAddWithProjectType };
